// @owners { team: patients-team }
import React, { useCallback, useContext } from 'react';
import { ActionSheetContext } from '../../../../../design-system/src';
import { CartUpdatedActionSheet } from '../../../../../features';
import { useRemoveFromCart } from '../../../../../features/src/cart/hooks/useRemoveFromCart';
import { usePrescriptionsInCart } from '~shared/features/cart/hooks/usePrescriptionsInCart';
import { useNextAvailableDateAddressId } from '~shared/features/next-available-date/hooks/useNextAvailableDateAddressId';
import { getIsScheduleableByPrescriptionID } from '~shared/features/prescriptions/selectors/getScheduleableForPrescriptionID';
import { getIsEditingExistingOrder } from '~shared/features/ui/selectors/getCart';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { useSelectorShared } from '~shared/store';

export const useRemoveUnschedulablePrescriptionsFromCart = () => {
  const { setActiveActionSheet } = useContext(ActionSheetContext);
  const { trackEvent } = useAnalytics();
  const { handleRemoveFromCart } = useRemoveFromCart();
  const { prescriptions } = usePrescriptionsInCart();
  const nextAvailableDateAddressID = useNextAvailableDateAddressId();
  const isScheduleableByPrescriptionID = useSelectorShared((state) =>
    getIsScheduleableByPrescriptionID(state, { nextAvailableDateAddressID }),
  );
  const isEditingOrder = useSelectorShared(getIsEditingExistingOrder);

  const removeUnschedulablePrescriptions = useCallback(() => {
    let removedItems = false;

    // if we are editing an order, we are in post checkout and should not remove unschedulable meds
    if (isEditingOrder) return;

    prescriptions.forEach((prescription) => {
      const isScheduleable = isScheduleableByPrescriptionID[prescription.id];

      if (!isScheduleable) {
        removedItems = true;
        trackEvent({
          event: EVENTS.CART__UNSCHEDULABLE_PRESCRIPTION_REMOVED,
          params: {},
          additionalFields: { prescriptionId: prescription.id },
        });

        handleRemoveFromCart({
          item: {
            resource_type: 'Prescription',
            resource_id: prescription.id,
            prescription,
          },
        });
      }
    });

    if (removedItems) {
      setActiveActionSheet(<CartUpdatedActionSheet />);
    }
  }, [
    isEditingOrder,
    prescriptions,
    isScheduleableByPrescriptionID,
    trackEvent,
    handleRemoveFromCart,
    setActiveActionSheet,
  ]);

  return { removeUnschedulablePrescriptions };
};
